/**
 * Find an item in an array, given its id.
 * @param array
 * @param id
 */
export function findById<T extends { id: number }>(array: Array<T>, id: T["id"]): T | undefined {
  return array.find((item) => item.id === id)
}

/**
 * Get an array of ids from a collection of objects with ids.
 * @param array
 */
export function getIds<T extends { id: number }>(array: Array<T>): Array<T["id"]> {
  return array.map(({ id }) => id)
}

/**
 * Insert item in array at given position.
 * @param array
 * @param item
 * @param position
 */
export function insertItemInArray<T>(array: Array<T>, item: T, position: number): Array<T> {
  const newArray = [...array]
  newArray.splice(position, 0, item)
  return newArray
}

/**
 * Move and update an item in an array.
 * @param array
 * @param item
 * @param newPosition The new position.
 * @param newItem The new item. Leave blank to keep the item unchanged.
 */
export function moveItemWithinArray<T>(array: Array<T>, item: T, newPosition: number, newItem: T = item): Array<T> {
  const arrayClone = [...array]
  const position = arrayClone.indexOf(item)
  arrayClone.splice(position, 1)
  arrayClone.splice(newPosition, 0, newItem)
  return arrayClone
}

/**
 * Modulo implementation that fixes JS strange behaviour with negative numbers.
 * See https://stackoverflow.com/questions/4467539/javascript-modulo-gives-a-negative-result-for-negative-numbers
 * @param n
 * @param m
 */
export function modulo(n: number, m: number): number {
  return ((n % m) + m) % m
}

/**
 * Returns the next item in the array. It cycles to the first item when the end of the array is reached.
 * @param array An array of items.
 * @param item The current item. If undefined, the first item is returned.
 */
export function nextItemInArray<T>(array: Array<T>, item: T): T | undefined {
  if (array.length === 0) return undefined
  const position = array.indexOf(item)
  return array[(position + 1) % array.length]
}

/**
 * Returns the previous item in the array. It cycles to the last item when the beginning of the array is reached.
 * @param array An array of items.
 * @param item The current item. If undefined, the last item is returned.
 */
export function prevItemInArray<T>(array: Array<T>, item: T): T | undefined {
  if (array.length === 0) return undefined
  const position = array.indexOf(item)
  return array[position === -1 ? array.length - 1 : modulo(position - 1, array.length)]
}

/**
 * Replace an item with a given id in an array, without changing its position.
 * @param array
 * @param id
 * @param newItem
 */
export function replaceInArrayById<T extends { id: number }>(
  array: Array<T>,
  id: T["id"],
  newItem: (item: T) => T,
): Array<T> {
  return array.map((element) => (element.id === id ? newItem(element) : element))
}

/**
 * Replace an item in an array by a new item, without changing its position.
 * @param array
 * @param item
 * @param newItem
 */
export function replaceItemInArray<T>(array: Array<T>, item: T, newItem: T): Array<T> {
  return array.map((element) => (element === item ? newItem : element))
}

/**
 * Returns the array minus the item with given id.
 * @param array
 * @param id
 */
export function removeInArrayById<T extends { id: number }>(array: Array<T>, id: T["id"]): Array<T> {
  return array.filter((element) => element.id !== id)
}

/**
 * Returns the array minus the item.
 * @param array
 * @param item
 */
export function removeItemInArray<T>(array: Array<T>, item: T): Array<T> {
  return array.filter((element) => element !== item)
}

/**
 * Shallow compare two objects.
 * @param obj1
 * @param obj2
 */
export function shallowEqual<TObject extends object>(obj1: TObject, obj2: TObject): boolean {
  const keys = Object.keys(obj1) as Array<keyof TObject>
  for (const key of keys) {
    if (obj1[key] !== obj2[key]) return false
  }
  return true
}

/**
 * Returns the nth percentile of an array of numbers.
 * @param array
 * @param n
 */
export function nthPercentile(array: Array<number>, n: number): number {
  return array.sort((a, b) => a - b)[Math.floor((array.length * n) / 100)]
}

/**
 * Returns the last element of an array that matches the predicate or the first element that does not match.
 * Returns undefined if no element matches, or the array only contains matching elements.
 * @param collection
 * @param predicate
 */
export function findLastBeforeMatch<T>(collection: T[], predicate: (element: T) => boolean): T | undefined {
  for (let i = collection.length - 1; i >= 0; i--) {
    if (predicate(collection[i])) {
      if (i > 0 && !predicate(collection[i - 1])) {
        return collection[i - 1]
      } else {
        return collection.find((element) => !predicate(element))
      }
    }
  }
  return undefined
}
